import isEmpty from 'lodash/isEmpty';
import type { SearchResultHit, ClientItem } from '@mimir/common/dto/searchResultHit';
import type { FolderPathSegmentDto } from '@mimir/common/dto/folderModels';
import { convertUnixPathToWindowsLike } from '../upload/premiere';
import { encodeForFs } from '@mimir/common/util/fs';

function getItemPathPrefix(prefix: string | null) {
  let itemPathPrefix = '';
  if (prefix && !isEmpty(prefix)) {
    itemPathPrefix = prefix;

    let lastChar = itemPathPrefix.slice(-1);
    while (lastChar == '/' || lastChar == '\\') {
      itemPathPrefix = itemPathPrefix.slice(0, -1);
      lastChar = itemPathPrefix.slice(-1);
    }
  }
  return itemPathPrefix;
}

class PathBuilder {
  readonly itemPathPrefix: string;

  constructor(prefix: string | null, readonly pathSeparator: string) {
    this.itemPathPrefix = getItemPathPrefix(prefix);
  }

  makePath(pathArray: FolderPathSegmentDto[]) {
    let thisPath = pathArray.map((folder: FolderPathSegmentDto) => folder.name).join(this.pathSeparator);

    // itemPathResponse.path should have a leading slash but not trailing one
    thisPath = `${this.itemPathPrefix}${this.pathSeparator}${thisPath}`;

    if (this.pathSeparator === '\\') {
      thisPath = convertUnixPathToWindowsLike(thisPath);
    }
    return thisPath;
  }
}

/**
 * Returns local paths without trailing slash. Paths need to be resolved already.
 * @param item The search hit to generate path for.
 * @param prefix Prefix
 * @param paths The resolved folder paths
 */
export const getItemLocalDirsFromPaths = (
  item: SearchResultHit,
  prefix: string | null,
  pathSep: string,
  paths: FolderPathSegmentDto[][],
): string[] => {

  if (!item.folderParents || item.folderParents.length === 0) {
    return [];
  }
  const pathBuilder = new PathBuilder(prefix, pathSep);

  return paths.map(pathArray => {
    const encodedPathArray = pathArray.map(oldSegment => {
      const segment = { ...oldSegment };
      segment.name = encodeForFs(segment.name);
      return segment;
    });
    return pathBuilder.makePath(encodedPathArray);
  });
};

/**
 * Returns local paths without trailing slash
 * @param item
 * @param fetchPathAsyncFn
 * @param prefix
 */
export const getItemLocalDirs = async (
  item: Pick<ClientItem, 'id' | 'folderParents'>,
  prefix: string | null,
  pathSep: string,
  getAncestry: (itemId: string) => Promise<FolderPathSegmentDto[][]>
): Promise<string[]> => {
  if (!item.folderParents || item.folderParents.length === 0) {
    return [];
  }

  const pathBuilder = new PathBuilder(prefix, pathSep);
  const paths = await getAncestry(item.id!);
  return paths.map(pathArray => {
    const encodedPathArray = pathArray.map(oldSegment => {
      const segment = { ...oldSegment };
      segment.name = encodeForFs(segment.name);
      return segment;
    });
    return pathBuilder.makePath(encodedPathArray);
  });
};
